import React, { useEffect, useState } from 'react';
import { useStore } from '../../context/StoreContext';
import storeService from '../../services/storeService';
import inventoryService from '../../services/inventoryService';
import transfersService from '../../services/transfersService';

const HandheldTransfersPage = () => {
  const { activeStore } = useStore();
  const [stores, setStores] = useState([]);
  const [destinationStore, setDestinationStore] = useState('');
  const [lookup, setLookup] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [items, setItems] = useState([]);
  const [notes, setNotes] = useState('');
  const [status, setStatus] = useState({ type: '', message: '' });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadStores = async () => {
      try {
        const data = await storeService.getStores();
        setStores(data);
      } catch (err) {
        console.error(err);
        setStatus({ type: 'error', message: 'Unable to load stores.' });
      }
    };
    loadStores();
  }, []);

  const handleAddItem = async () => {
    if (!activeStore?._id) {
      setStatus({ type: 'error', message: 'Select a source store first.' });
      return;
    }
    if (!lookup.trim()) {
      setStatus({ type: 'error', message: 'Scan or enter a SKU to add.' });
      return;
    }
    if (quantity <= 0) {
      setStatus({ type: 'error', message: 'Quantity must be at least 1.' });
      return;
    }
    try {
      const products = await inventoryService.searchProducts(activeStore._id, lookup.trim());
      const match =
        products.find(
          (p) =>
            p.barcode?.toLowerCase() === lookup.trim().toLowerCase() ||
            p.sku?.toLowerCase() === lookup.trim().toLowerCase()
        ) || products[0];
      if (!match) {
        setStatus({ type: 'error', message: 'Product not found.' });
        return;
      }
      setItems((prev) => [
        ...prev,
        { productId: match._id, name: match.name, quantity, stock: match.stock ?? 0 },
      ]);
      setLookup('');
      setQuantity(1);
      setStatus({ type: '', message: '' });
    } catch (err) {
      console.error(err);
      setStatus({ type: 'error', message: 'Unable to add item.' });
    }
  };

  const handleRemoveItem = (id) => {
    setItems(items.filter((item) => item.productId !== id));
  };

  const handleCreateTransfer = async () => {
    if (!activeStore?._id || !destinationStore) {
      setStatus({ type: 'error', message: 'Select both source and destination stores.' });
      return;
    }
    if (items.length === 0) {
      setStatus({ type: 'error', message: 'Add at least one item.' });
      return;
    }
    setLoading(true);
    try {
      await transfersService.createTransfer({
        fromStore: activeStore._id,
        toStore: destinationStore,
        items: items.map((item) => ({
          product: item.productId,
          quantity: item.quantity,
          name: item.name,
        })),
        notes,
      });
      setItems([]);
      setNotes('');
      setStatus({ type: 'success', message: 'Transfer created and awaiting approval.' });
    } catch (err) {
      console.error(err);
      setStatus({ type: 'error', message: err.response?.data?.message || 'Failed to create transfer.' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 space-y-4">
      <div className="bg-white rounded-2xl shadow p-4 space-y-3">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-slate-400">From</label>
            <input
              type="text"
              value={activeStore?.name || 'Select store'}
              disabled
              className="w-full border border-gray-300 rounded-xl px-3 py-2 mt-1 bg-gray-100"
            />
          </div>
          <div>
            <label className="text-xs uppercase tracking-[0.3em] text-slate-400">To</label>
            <select
              value={destinationStore}
              onChange={(e) => setDestinationStore(e.target.value)}
              className="w-full border border-gray-300 rounded-xl px-3 py-2 mt-1"
            >
              <option value="">Select destination</option>
              {stores
                .filter((store) => store._id !== activeStore?._id)
                .map((store) => (
                  <option key={store._id} value={store._id}>
                    {store.name}
                  </option>
                ))}
            </select>
          </div>
        </div>
        {status.message && (
          <p className={status.type === 'error' ? 'text-sm text-red-600' : 'text-sm text-green-600'}>
            {status.message}
          </p>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow p-4 space-y-3">
        <p className="text-xs uppercase tracking-[0.3em] text-slate-400">Add items</p>
        <div className="flex gap-2">
          <input
            type="text"
            value={lookup}
            onChange={(e) => setLookup(e.target.value)}
            className="flex-1 border border-gray-300 rounded-xl px-3 py-2"
            placeholder="Scan barcode / enter SKU"
          />
          <input
            type="number"
            min="1"
            value={quantity}
            onChange={(e) => setQuantity(parseInt(e.target.value, 10) || 1)}
            className="w-24 border border-gray-300 rounded-xl px-3 py-2"
          />
          <button
            type="button"
            onClick={handleAddItem}
            className="px-4 py-2 rounded-xl bg-slate-900 text-white"
          >
            Add
          </button>
        </div>
        {items.length > 0 && (
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {items.map((item) => (
              <div key={item.productId} className="border rounded-xl p-3 flex justify-between items-center">
                <div>
                  <p className="font-semibold">{item.name}</p>
                  <p className="text-xs text-slate-500">
                    Qty: {item.quantity} | On hand: {item.stock}
                  </p>
                </div>
                <button onClick={() => handleRemoveItem(item.productId)} className="text-red-500 text-sm">
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl shadow p-4 space-y-3">
        <label className="text-xs uppercase tracking-[0.3em] text-slate-400">Notes</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={3}
          className="w-full border border-gray-300 rounded-xl px-3 py-2"
          placeholder="Driver, tote ID, etc."
        />
        <button
          type="button"
          onClick={handleCreateTransfer}
          disabled={loading}
          className="w-full py-3 rounded-xl bg-amber-500 text-white font-semibold hover:bg-amber-600 disabled:bg-gray-400"
        >
          {loading ? 'Submitting…' : 'Create transfer'}
        </button>
      </div>
    </div>
  );
};

export default HandheldTransfersPage;
